import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import RetentionVerifyDialog from "@/components/collection/RetentionVerifyDialog";

const inp = "border border-slate-300 px-2 h-8 text-xs bg-white";
const EMPTY = { q: "", client: "", project: "", link_status: "", payment_status: "", due_state: "", pic: "", min_age_days: "" };
const LINK_TONE = { explicitly_linked: "bg-slate-100 text-slate-700 border-slate-300", project_fallback: "bg-amber-50 text-amber-800 border-amber-300", unlinked: "bg-amber-50 text-amber-800 border-amber-300" };

const Card = ({ label, value, sub, onClick, active, testid }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
    className={`text-left border px-3 py-2 bg-white ${active ? "border-[#FF5A1F] ring-1 ring-[#FF5A1F]" : "border-slate-200"} ${onClick ? "hover:border-slate-400" : "cursor-default"}`}>
    <div className="text-[10px] uppercase text-slate-500">{label}</div>
    <div className="font-bold text-sm text-[#0B1D3A] tabular-nums">{value}</div>
    {sub && <div className="text-[10px] text-slate-500 tabular-nums">{sub}</div>}
  </button>
);

const Summary = ({ s, f, setF }) => {
  const pick = (patch) => setF({ ...EMPTY, ...patch });
  const on = (k, v) => f[k] === v;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="ret-summary">
      <Card testid="ret-card-needs" label="Perlu Verifikasi" value={`${s.needs_verification?.count ?? 0} klaim`} active={!Object.values(f).some(Boolean)}
        sub={`Sisa ${formatIDR(s.needs_verification?.remaining_total ?? 0)} · neto ${formatIDR(s.needs_verification?.net_total ?? 0)}`} onClick={() => pick({})} />
      <Card testid="ret-card-fallback" label="Pemetaan Sementara" value={`${s.project_fallback?.count ?? 0} klaim`} sub={formatIDR(s.project_fallback?.total ?? 0)}
        active={on("link_status", "project_fallback")} onClick={() => pick({ link_status: "project_fallback" })} />
      <Card testid="ret-card-unlinked" label="Tautan Sumber Belum Dikonfirmasi" value={`${s.unlinked?.count ?? 0} klaim`} sub={formatIDR(s.unlinked?.total ?? 0)}
        active={on("link_status", "unlinked")} onClick={() => pick({ link_status: "unlinked" })} />
      <Card testid="ret-card-nodue" label="Jatuh Tempo Belum Diisi" value={`${s.due_date_unknown?.count ?? 0} klaim`} sub={`Sisa ${formatIDR(s.due_date_unknown?.remaining_total ?? 0)}`}
        active={on("due_state", "jatuh_tempo_belum_diisi")} onClick={() => pick({ due_state: "jatuh_tempo_belum_diisi" })} />
      <Card testid="ret-card-held" label="Retensi Ditahan" value={formatIDR(s.retention_held ?? 0)} />
      <Card testid="ret-card-unclaimed" label="Retensi Belum Diklaim" value={formatIDR(s.retention_unclaimed ?? 0)} sub={`JT belum diketahui ${formatIDR(s.retention_unclaimed_due_unknown ?? 0)}`} />
      <Card testid="ret-card-claimed" label="Klaim Diajukan" value={formatIDR(s.retention_claimed ?? 0)} sub={`Dibayar ${formatIDR(s.retention_claim_paid ?? 0)}`} />
      <Card testid="ret-card-outstanding" label="Sisa Klaim" value={formatIDR(s.retention_claim_outstanding ?? 0)} sub="klaim − pembayaran klaim" />
    </div>
  );
};

const Filters = ({ f, setF }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="ret-filter-bar">
      <input placeholder="Cari nomor/proyek/client" value={f.q} onChange={set("q")} className={`${inp} w-44`} data-testid="ret-filter-q" />
      <input placeholder="Proyek/WO" value={f.project} onChange={set("project")} className={`${inp} w-32`} data-testid="ret-filter-project" />
      <input placeholder="Client" value={f.client} onChange={set("client")} className={`${inp} w-28`} data-testid="ret-filter-client" />
      <select value={f.link_status} onChange={set("link_status")} className={inp} data-testid="ret-filter-link">
        <option value="">Semua tautan</option><option value="explicitly_linked">Tertaut ke Sumber</option>
        <option value="project_fallback">Pemetaan Sementara</option><option value="unlinked">Belum Dikonfirmasi</option>
      </select>
      <select value={f.payment_status} onChange={set("payment_status")} className={inp} data-testid="ret-filter-payment">
        <option value="">Semua pembayaran</option><option value="menunggu_pembayaran">Belum Dibayar</option>
        <option value="dibayar_sebagian">Dibayar Sebagian</option><option value="dicairkan">Lunas</option>
      </select>
      <select value={f.due_state} onChange={set("due_state")} className={inp} data-testid="ret-filter-due">
        <option value="">Semua jatuh tempo</option><option value="jatuh_tempo_belum_diisi">Jatuh Tempo Belum Diisi</option>
        <option value="belum_jatuh_tempo">Belum Jatuh Tempo</option><option value="lewat_jatuh_tempo">Lewat Jatuh Tempo</option>
      </select>
      <input placeholder="PIC" value={f.pic} onChange={set("pic")} className={`${inp} w-24`} data-testid="ret-filter-pic" />
      <input type="number" min={0} placeholder="Umur ≥ hari" value={f.min_age_days} onChange={set("min_age_days")} className={`${inp} w-24`} data-testid="ret-filter-age" />
    </div>
  );
};

const ClaimRow = ({ r, onOpen }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`ret-row-${r.entry_id}`}>
    <td className="px-2 py-2"><div className={r.invoice_number ? "font-semibold" : "italic text-amber-700"}>{r.invoice_number || "Nomor klaim belum diisi"}</div>
      <div className="text-[10px] text-slate-500">{r.claim_date ? fmtDate(r.claim_date) : "Tanggal klaim belum diisi"}</div></td>
    <td className="px-2 py-2"><div>{r.client || "—"}</div><div className="text-[10px] text-slate-500">{r.project_name}</div></td>
    <td className="px-2 py-2 text-right tabular-nums">{r.bruto_known ? formatIDR(r.cash_due_idr) : "—"}</td>
    <td className="px-2 py-2 text-right tabular-nums">{formatIDR(r.received_idr)}</td>
    <td className="px-2 py-2 text-right tabular-nums font-semibold">{formatIDR(r.remaining_idr)}</td>
    <td className="px-2 py-2 text-[10px]"><span className={`inline-block border px-1.5 py-0.5 ${LINK_TONE[r.link_status]}`}>{r.link_status_label}</span>
      <div className="mt-1 text-slate-600">{r.payment_status_label}</div>
      <div className={r.due_state === "jatuh_tempo_belum_diisi" ? "text-amber-700" : "text-slate-600"}>{r.due_date ? fmtDate(r.due_date) : r.due_state_label}</div></td>
    <td className="px-2 py-2 max-w-xs"><div className="flex flex-wrap gap-1">{r.reason_labels.map((x) => <span key={x} className="text-[10px] border border-amber-300 bg-amber-50 text-amber-800 px-1">{x}</span>)}</div></td>
    <td className="px-2 py-2 text-[10px]">{r.claim_pic || "—"}<div className="text-slate-500">{r.age_days ?? "—"} hr</div></td>
    <td className="px-2 py-2"><button onClick={() => onOpen(r)} className="px-2 py-1 bg-[#0B1D3A] text-white text-[10px] uppercase" data-testid={`ret-open-${r.entry_id}`}>Periksa</button></td>
  </tr>
);

/** Antrean kanonis Klaim Retensi Perlu Verifikasi (GET /collection/retention-claims). */
export default function RetentionClaimQueue({ onChanged, initialProject }) {
  const [f, setF] = useState({ ...EMPTY, project: initialProject || "" });
  const [data, setData] = useState({ items: [], summary: {}, filtered: {} });
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const { data: d } = await api.get("/collection/retention-claims", { params });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat antrean klaim retensi."); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const fs = data.filtered || {};
  return (
    <div className="space-y-3" data-testid="ret-queue">
      <Summary s={data.summary || {}} f={f} setF={setF} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Filters f={f} setF={setF} />
        <div className="text-[11px] text-slate-600" data-testid="ret-filtered-summary">
          Ditampilkan {fs.count ?? 0} klaim · neto {formatIDR(fs.net_total || 0)} · sisa {formatIDR(fs.remaining_total || 0)}
          {Object.values(f).some(Boolean) && <button onClick={() => setF(EMPTY)} className="ml-2 text-sky-700 underline" data-testid="ret-clear">Reset</button>}
        </div>
      </div>
      <div className="border border-slate-200 bg-white overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-600"><tr className="text-left">
            <th className="px-2 py-2">Klaim</th><th className="px-2 py-2">Client · Proyek/WO</th><th className="px-2 py-2 text-right">Neto Klaim</th>
            <th className="px-2 py-2 text-right">Dibayar</th><th className="px-2 py-2 text-right">Sisa</th><th className="px-2 py-2">Tautan · Bayar · Jatuh Tempo</th>
            <th className="px-2 py-2">Alasan Perlu Verifikasi</th><th className="px-2 py-2">PIC · Umur</th><th className="px-2 py-2" />
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="p-4 text-center text-slate-400">Memuat…</td></tr>
              : data.items.length === 0 ? <tr><td colSpan={9} className="p-4 text-center text-slate-400" data-testid="ret-empty">Tidak ada klaim retensi perlu verifikasi.</td></tr>
                : data.items.map((r) => <ClaimRow key={r.entry_id} r={r} onOpen={setTarget} />)}
          </tbody>
        </table>
      </div>
      {target && <RetentionVerifyDialog target={target} canVerify={data.can_verify}
        onClose={() => setTarget(null)} onChanged={() => { load(); onChanged?.(); }} />}
    </div>
  );
}
